import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Modal } from '@core/enums/modal';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { IDomain2025, IDomainMeasureRequest } from '@core/interfaces/idomainMeasure';
import { IModalEmitData } from '@core/interfaces/imodal';
import { ISearchInput } from '@core/interfaces/isearch-input';
import { LookupService } from '@core/services/lookup.service';
import { ToastService } from '@core/services/toast.service';
import { take } from 'rxjs';
import { AddActivityBankComponent } from './add-activity-bank/add-activity-bank.component';
import { EditActivityBankComponent } from './edit-activity-bank/edit-activity-bank.component';
import { ActivityBankService } from '@core/services/activity-bank.service';
import { IActivityBank, IActivityBankSearchResponseDto } from '@core/interfaces/iactivity-bank';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'drdp-activity-bank',
  templateUrl: './activity-bank.component.html',
  styleUrls: ['./activity-bank.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActivityBankComponent implements OnInit {
  private activityBankService = inject(ActivityBankService);
  private fb = inject(FormBuilder);
  private toast = inject(ToastService);
  private modal = inject(MatDialog);
  private translocoService = inject(TranslocoService);
  searchInput: ISearchInput = {
      searchName: true,
      searchDomain: true,
      searchMeasure: true,
      searchLevel: true
    };
  activityBankForm: FormGroup | any;
  clearDropdown = false;
  searchResults: IActivityBankSearchResponseDto[] = [];

  get searchBy() {
    return this.activityBankForm.get('searchBy');
  }
  get domainId() {
    return this.activityBankForm.get('domainId');
  }
  get measureIds() {
    return this.activityBankForm.get('measureIds');
  }
  get developmentalLevelIds() {
    return this.activityBankForm.get('developmentalLevelIds');
  }
  constructor() { }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    this.activityBankForm = this.fb.group({
      searchBy: null,
      domainId: null,
      measureIds: [],
      developmentalLevelIds: []
    });
  }

  onClear(): void {
    this.activityBankForm.reset();
    this.searchResults = [];
    this.clearDropdown = true;
  }

  openAddModal(domain: IActivityBankSearchResponseDto): void {
    const modalRef = this.modal.open(AddActivityBankComponent, {
      width: '600px',
      data: {
        domainId: domain.domainId,
        domainName: domain.domainName,
        iconName: domain.iconName
      }
    });

    modalRef.afterClosed().subscribe((res: any) => {
      if (res?.formValid) {
        this.activityBankService.addDescription(res.payload).pipe(
          take(1)
        ).subscribe({
          next: () => {
            this.toast.success(this.translocoService.translate('configuration.activityBank.addedToast'));
            this.refreshSearch();
          },
          error: (error) => {
            console.error('Add error:', error);
            this.toast.error(this.translocoService.translate('configuration.activityBank.addFailedToast'));
          }
        });
      }
    });
  }

  openEditModal(domain: string, measure: string, description: string, spanishDescription: string, activityBankId: number, iconName: string): void {
    const modalData: any = {
      domain: domain,
      measure: measure,
      description: description,
      spanishDescription: spanishDescription,
      activityBankId: activityBankId,
      iconName: iconName
    };
    const data: IModalEmitData = modalData;
    const modalRef = this.modal.open(EditActivityBankComponent, {
      data: data ?? undefined,
    });

    modalRef.afterClosed().subscribe((res: any) => {
      if (res?.formValid) {
        const updatePayload: IActivityBank = {
          id: activityBankId,
          activity: res.payload.activity,
          spanishActivity: res.payload.spanishActivity
        };

        this.activityBankService.updateDescription(updatePayload).pipe(
          take(1)
        ).subscribe({
          next: () => {
            this.toast.success(this.translocoService.translate('configuration.activityBank.updatedToast'));
            this.refreshSearch();
          },
          error: (error) => {
            console.error('Update error:', error);
            this.toast.error(this.translocoService.translate('configuration.activityBank.updateFailedToast'));
          }
        });
      }
    });
  }

  openDeleteModal(domain: string, measure: string, description: string, activityBankId: number): void {
    const modalRef = this.modal.open(ConfirmationModalComponent, {
      data: {
        data: {
          id: activityBankId,
          modalInfo: {
            title: this.translocoService.translate('configuration.activityBank.deleteTitle'),
            message: this.translocoService.translate('configuration.activityBank.deleteMessage', { domain, measure, description }),
            primaryBtnClass: 'red',
            primaryBtnText: Modal.Delete,
            showCancel: true,
            cancelText: this.translocoService.translate('configuration.common.cancel')
          }
        }
      }
    });
  
    modalRef.afterClosed().subscribe((result: any) => {
      if (result?.id) {
        this.deleteActivity(result.id);
      }
    });
  }

  deleteActivity(activityBankId: number): void {
    this.activityBankService.deleteDescription(activityBankId).pipe(
      take(1)
    ).subscribe({
      next: () => {
        this.toast.success(this.translocoService.translate('configuration.activityBank.deletedToast'));
        this.refreshSearch();
      },
      error: (error) => {
        console.error('Delete error:', error);
        this.toast.error(this.translocoService.translate('configuration.activityBank.deleteFailedToast'));
      }
    });
  }

  refreshSearch(): void {
    const payload = this.activityBankForm.value;
    this.activityBankService.search(payload).pipe(
      take(1)
    ).subscribe({
      next: (data) => {
        this.searchResults = data;
      },
      error: (error) => {
        console.error('Search error:', error);
      }
    });
  }

  search(event: any): void {
    if (this.activityBankForm.invalid) {
      return;
    }
    this.searchBy?.setValue(event.SearchName?.trim() || null);
    this.domainId?.setValue(event.domainId ? event.domainId : null);
    this.measureIds?.setValue(event.measureIds ? event.measureIds : []);
    this.developmentalLevelIds?.setValue(event.levelIds ? event.levelIds : []);
    const payload = this.activityBankForm.value;

    this.activityBankService.search(payload).pipe(
      take(1) 
    ).subscribe({
      next: (data) => {
        this.searchResults = data;
      },
      error: (error) => {
        console.error('Search error:', error);
      }
    });
  }
}